import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { CheckoutModal } from './CheckoutModal';
import { trackEvent } from '../../lib/analytics';
import type { NudgeType } from '../../hooks/useConversionTriggers';

interface PlanData {
  id: string;
  name: string;
  slug: string;
  price_brl: number;
  periodicity: string;
  is_featured: boolean;
  accounts_limit: number;
  cards_limit: number;
  monthly_transactions_limit: number;
  goals_limit: number;
  reports_enabled: boolean;
}

interface UpgradeModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  /** Conversion tracking context */
  trigger?: NudgeType | string;
  resource?: string;
  feature?: string;
}

const formatLimit = (v: number): string => (v === -1 ? 'Ilimitado' : String(v));
const formatLimitLabel = (feature: string, v: number): string => {
  if (v === -1) {
    const map: Record<string, string> = {
      accounts_limit: 'Contas ilimitadas',
      cards_limit: 'Cartões ilimitados',
      monthly_transactions_limit: 'Lançamentos ilimitados',
      goals_limit: 'Metas ilimitadas',
    };
    return map[feature] || 'Ilimitado';
  }
  const labels: Record<string, string> = {
    accounts_limit: v === 1 ? '1 conta bancária' : `${v} contas bancárias`,
    cards_limit: v === 1 ? '1 cartão de crédito' : `${v} cartões de crédito`,
    monthly_transactions_limit: `${v} lançamentos por mês`,
    goals_limit: v === 1 ? '1 meta financeira' : `${v} metas financeiras`,
  };
  return labels[feature] || String(v);
};

const FEATURE_ICONS: Record<string, string> = {
  accounts_limit: 'ri-bank-line',
  cards_limit: 'ri-bank-card-line',
  monthly_transactions_limit: 'ri-exchange-funds-line',
  goals_limit: 'ri-focus-3-line',
  reports_enabled: 'ri-bar-chart-2-line',
  export: 'ri-download-line',
};

// ── Contextual benefit highlight based on what triggered the upgrade ────────
const getContextualHighlight = (trigger?: string, resource?: string, feature?: string): {
  icon: string; title: string; items: string[]
} | null => {
  if (trigger === 'limit_hit' || trigger === 'near_limit') {
    const resourceMap: Record<string, { icon: string; title: string; items: string[] }> = {
      transactions: {
        icon: 'ri-exchange-funds-line',
        title: 'Lançamentos ilimitados',
        items: ['Registre sem limite todos os meses', 'Histórico completo sem interrupção', 'Relatórios com todos os dados'],
      },
      accounts: {
        icon: 'ri-bank-line',
        title: 'Quantas contas quiser',
        items: ['Adicione todos os seus bancos', 'Controle poupança, investimento e mais', 'Extrato unificado de todas as contas'],
      },
      cards: {
        icon: 'ri-bank-card-line',
        title: 'Cartões ilimitados',
        items: ['Todos os seus cartões no mesmo lugar', 'Controle de fatura e limite', 'Pagamento de fatura integrado'],
      },
      goals: {
        icon: 'ri-focus-3-line',
        title: 'Metas ilimitadas',
        items: ['Defina objetivos sem restrição', 'Acompanhe cada meta individualmente', 'Insights de progresso e prazo'],
      },
    };
    if (resource && resourceMap[resource]) return resourceMap[resource];
  }

  if (trigger === 'feature_block' || trigger === 'report_interest' ||
      feature === 'canAccessReports') {
    return {
      icon: 'ri-bar-chart-2-line',
      title: 'Relatórios completos desbloqueados',
      items: [
        'Gráficos de receitas vs despesas',
        'Análise por categoria, conta e cartão',
        'Projeção de saldo e fluxo de caixa',
        'Exportação para CSV e PDF',
      ],
    };
  }

  if (trigger === 'tenure') {
    return {
      icon: 'ri-star-line',
      title: 'Tudo que você ganha ao fazer upgrade',
      items: [
        'Lançamentos e contas ilimitados',
        'Relatórios avançados com gráficos',
        'Exportação para CSV e PDF',
        'Suporte prioritário',
      ],
    };
  }

  return null;
};

export const UpgradeModal = ({
  open,
  onClose,
  title,
  description,
  trigger,
  resource,
  feature,
}: UpgradeModalProps) => {
  const [showCheckout, setShowCheckout] = useState(false);
  const [freePlan, setFreePlan] = useState<PlanData | null>(null);
  const [featuredPaidPlan, setFeaturedPaidPlan] = useState<PlanData | null>(null);
  const [allPaidPlans, setAllPaidPlans] = useState<PlanData[]>([]);
  const [plansLoading, setPlansLoading] = useState(true);

  useEffect(() => {
    if (!open) return;
    setPlansLoading(true);
    // Track impression
    trackEvent('paywall_impression', { trigger: trigger ?? 'direct', resource, feature });

    supabase
      .from('plans')
      .select('id, name, slug, price_brl, periodicity, is_featured, accounts_limit, cards_limit, monthly_transactions_limit, goals_limit, reports_enabled')
      .eq('is_active', true)
      .eq('is_public', true)
      .order('display_order')
      .then(({ data }) => {
        const all = (data as PlanData[]) || [];
        const free = all.find(p => Number(p.price_brl) === 0) || null;
        const paid = all.filter(p => Number(p.price_brl) > 0);
        setFreePlan(free);
        setAllPaidPlans(paid);
        setFeaturedPaidPlan(paid.find(p => p.is_featured) || paid[0] || null);
        setPlansLoading(false);
      });
  }, [open]);

  const handleClose = () => {
    trackEvent('upgrade_dismiss', { trigger: trigger ?? 'direct' });
    onClose();
  };

  if (!open) return null;

  const hasContext = !!(title || description);
  const cheapestPrice = allPaidPlans.length > 0
    ? Math.min(...allPaidPlans.map(p => Number(p.price_brl)))
    : null;

  const contextHighlight = getContextualHighlight(trigger, resource, feature);

  const LIMIT_FEATURES: Array<{ key: keyof PlanData; icon: string }> = [
    { key: 'accounts_limit', icon: 'ri-bank-line' },
    { key: 'cards_limit', icon: 'ri-bank-card-line' },
    { key: 'goals_limit', icon: 'ri-focus-3-line' },
    { key: 'monthly_transactions_limit', icon: 'ri-exchange-funds-line' },
  ];

  const buildFreeFeatureRows = () => {
    if (!freePlan) return [];
    return [
      ...LIMIT_FEATURES.map(f => ({
        label: formatLimitLabel(f.key, freePlan[f.key] as number),
        included: true,
        icon: f.icon,
      })),
      { label: 'Dashboard básico', included: true, icon: 'ri-dashboard-line' },
      { label: 'Relatórios avançados', included: freePlan.reports_enabled, icon: 'ri-bar-chart-2-line' },
      { label: 'Exportar dados', included: false, icon: 'ri-download-line' },
      { label: 'Suporte prioritário', included: false, icon: 'ri-shield-check-line' },
    ];
  };

  const buildPaidFeatureRows = (plan: PlanData) => {
    return [
      ...LIMIT_FEATURES.map(f => ({
        label: formatLimitLabel(f.key, plan[f.key] as number),
        icon: f.icon,
      })),
      { label: 'Dashboard completo', icon: 'ri-dashboard-line' },
      { label: 'Relatórios avançados', icon: 'ri-bar-chart-2-line' },
      { label: 'Exportar dados CSV', icon: 'ri-download-line' },
      { label: 'Suporte prioritário', icon: 'ri-shield-check-line' },
    ];
  };

  const freeFeatures = buildFreeFeatureRows();
  const paidFeatures = featuredPaidPlan ? buildPaidFeatureRows(featuredPaidPlan) : [];

  const handleGoToCheckout = () => {
    trackEvent('upgrade_click', { trigger: trigger ?? 'direct', resource, feature });
    setShowCheckout(true);
  };
  const handleCheckoutSuccess = () => { setShowCheckout(false); onClose(); };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />
        <div className="relative bg-white rounded-2xl max-w-lg w-full mx-4 overflow-hidden max-h-[92vh] flex flex-col">

          {/* Header */}
          <div className="bg-gradient-to-br from-emerald-500 to-teal-600 px-6 py-5 text-white flex-shrink-0">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                  <i className="ri-vip-crown-fill text-xl text-white" />
                </div>
                <div>
                  <h2 className="text-base font-700">{hasContext ? (title ?? 'Limite atingido') : 'Conheça os planos'}</h2>
                  <p className="text-emerald-100 text-xs mt-0.5">
                    {hasContext ? (description ?? 'Faça upgrade para continuar') : 'Desbloqueie tudo sem limitações'}
                  </p>
                </div>
              </div>
              <button onClick={handleClose} className="w-7 h-7 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 cursor-pointer">
                <i className="ri-close-line text-sm" />
              </button>
            </div>

            {/* Contextual highlight — shown only when trigger context is known */}
            {contextHighlight && !plansLoading && (
              <div className="mt-4 bg-white/15 rounded-xl px-4 py-3">
                <div className="flex items-center gap-2 mb-2">
                  <i className={`${contextHighlight.icon} text-white text-sm`} />
                  <p className="text-sm font-700 text-white">{contextHighlight.title}</p>
                </div>
                <div className="grid grid-cols-2 gap-1">
                  {contextHighlight.items.map((item) => (
                    <div key={item} className="flex items-center gap-1.5">
                      <i className="ri-check-line text-emerald-200 text-xs shrink-0" />
                      <span className="text-xs text-white/80 leading-tight">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="overflow-y-auto flex-1 px-6 py-5">
            {plansLoading ? (
              <div className="space-y-3">
                <div className="h-48 bg-gray-100 rounded-2xl animate-pulse" />
                <div className="h-32 bg-gray-100 rounded-2xl animate-pulse" />
              </div>
            ) : (
              <>
                {/* Comparison */}
                <div className="grid grid-cols-2 gap-4 mb-5">
                  {/* Free column */}
                  <div className="bg-gray-50 rounded-2xl p-4 border border-gray-100">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-7 h-7 bg-gray-200 rounded-lg flex items-center justify-center">
                        <i className="ri-user-line text-gray-500 text-sm" />
                      </div>
                      <p className="text-sm font-700 text-gray-700">{freePlan?.name ?? 'Gratuito'}</p>
                    </div>
                    <div className="space-y-2">
                      {freeFeatures.map(f => (
                        <div key={f.label} className="flex items-center gap-2">
                          <i className={`text-sm flex-shrink-0 ${f.included ? 'ri-check-line text-gray-500' : 'ri-close-line text-gray-300'}`} />
                          <span className={`text-xs ${f.included ? 'text-gray-600' : 'text-gray-300 line-through'}`}>{f.label}</span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 pt-3 border-t border-gray-200">
                      <p className="text-sm font-700 text-gray-500">R$ 0 <span className="font-400">/mês</span></p>
                    </div>
                  </div>

                  {/* Featured paid plan column */}
                  {featuredPaidPlan && (
                    <div className="bg-emerald-500 rounded-2xl p-4 relative overflow-hidden">
                      <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full" style={{ transform: 'translate(30%,-30%)' }} />
                      {/* "Most popular" badge */}
                      <div className="absolute top-3 right-3">
                        <span className="text-[10px] font-700 px-2 py-0.5 bg-white text-emerald-700 rounded-full">
                          ⭐ Popular
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-7 h-7 bg-white/20 rounded-lg flex items-center justify-center">
                          <i className="ri-vip-crown-fill text-white text-sm" />
                        </div>
                        <p className="text-sm font-700 text-white">{featuredPaidPlan.name}</p>
                      </div>
                      <div className="space-y-2">
                        {paidFeatures.map(f => (
                          <div key={f.label} className="flex items-center gap-2">
                            <i className="ri-check-line text-emerald-200 text-sm flex-shrink-0" />
                            <span className="text-xs text-emerald-50">{f.label}</span>
                          </div>
                        ))}
                      </div>
                      <div className="mt-4 pt-3 border-t border-white/20">
                        <p className="text-sm font-700 text-white">
                          R$ {Number(featuredPaidPlan.price_brl).toFixed(2).replace('.', ',')}
                          <span className="font-400 text-emerald-100">
                            {featuredPaidPlan.periodicity === 'annual' ? '/ano' : '/mês'}
                          </span>
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Extra paid plans if more than 1 */}
                {allPaidPlans.length > 1 && (
                  <div className="bg-gray-50 rounded-2xl p-4 mb-5">
                    <p className="text-xs font-700 text-gray-500 uppercase tracking-wide mb-3">Mais planos disponíveis</p>
                    <div className="space-y-2">
                      {allPaidPlans.filter(p => p.id !== featuredPaidPlan?.id).map(plan => (
                        <div key={plan.id} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <i className="ri-vip-crown-line text-emerald-500 text-sm" />
                            <span className="text-sm font-600 text-gray-700">{plan.name}</span>
                            <span className="text-xs text-gray-400">
                              · {formatLimit(plan.accounts_limit)} contas · {formatLimit(plan.cards_limit)} cartões
                            </span>
                          </div>
                          <span className="text-sm font-700 text-gray-800">
                            R$ {Number(plan.price_brl).toFixed(2).replace('.', ',')}
                            <span className="text-xs font-400 text-gray-400">
                              {plan.periodicity === 'annual' ? '/ano' : '/mês'}
                            </span>
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Perks highlight */}
                {featuredPaidPlan && (
                  <div className="bg-gray-50 rounded-2xl p-4 mb-5">
                    <p className="text-xs font-700 text-gray-500 uppercase tracking-wide mb-3">
                      Tudo que você desbloqueia no {featuredPaidPlan.name}
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { icon: FEATURE_ICONS.accounts_limit, label: formatLimitLabel('accounts_limit', featuredPaidPlan.accounts_limit) },
                        { icon: FEATURE_ICONS.cards_limit, label: formatLimitLabel('cards_limit', featuredPaidPlan.cards_limit) },
                        { icon: FEATURE_ICONS.goals_limit, label: formatLimitLabel('goals_limit', featuredPaidPlan.goals_limit) },
                        { icon: FEATURE_ICONS.monthly_transactions_limit, label: formatLimitLabel('monthly_transactions_limit', featuredPaidPlan.monthly_transactions_limit) },
                        ...(featuredPaidPlan.reports_enabled ? [{ icon: 'ri-bar-chart-2-line', label: 'Relatórios avançados' }] : []),
                        { icon: 'ri-download-line', label: 'Exportar dados CSV' },
                        { icon: 'ri-shield-check-line', label: 'Suporte prioritário' },
                        { icon: 'ri-global-line', label: 'Múltiplas moedas' },
                      ].map(p => (
                        <div key={p.label} className="flex items-center gap-2">
                          <div className="w-6 h-6 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <i className={`${p.icon} text-emerald-600 text-xs`} />
                          </div>
                          <span className="text-xs text-gray-700 font-500">{p.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer CTA */}
          <div className="px-6 py-4 border-t border-gray-100 flex-shrink-0 space-y-2">
            <button
              onClick={handleGoToCheckout}
              disabled={plansLoading || allPaidPlans.length === 0}
              className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white font-700 rounded-xl cursor-pointer whitespace-nowrap transition-colors text-sm flex items-center justify-center gap-2"
            >
              <i className="ri-vip-crown-line" />
              {cheapestPrice !== null
                ? `Fazer upgrade — a partir de R$ ${cheapestPrice.toFixed(2).replace('.', ',')}/mês`
                : 'Ver planos disponíveis'}
            </button>
            <button
              onClick={handleClose}
              className="w-full py-2.5 text-gray-400 hover:text-gray-600 font-500 text-sm transition-colors cursor-pointer whitespace-nowrap"
            >
              Continuar no plano gratuito
            </button>
          </div>
        </div>
      </div>

      <CheckoutModal
        open={showCheckout}
        onClose={() => setShowCheckout(false)}
        onSuccess={handleCheckoutSuccess}
        initialPlanId={featuredPaidPlan?.id}
      />
    </>
  );
};
